/**
 * Bộ helper dùng chung cho Test UI — thao tác như tester thật, không Lưu/Xóa dữ liệu.
 */
import { captureNode } from '@/modules/test-module-chitiet/tester/captureShot'
import { findButton, inspectFormChrome, sidebarOf } from '@/modules/test-module-chitiet/tester/layoutAudit'
import type { TesterShot } from '@/modules/test-module-chitiet/tester/shotStore'
import {
  clickElement,
  findInRoot,
  pressEnter,
  typeSlowly,
  waitFor,
  waitForDialogClosed,
  waitForTopDialog,
} from '@/modules/ui-test/utils/uiTestDomActions'
import type { UiTestFeatureDef, UiTestRunner } from '@/modules/ui-test/utils/uiTestRunner'
import { runUiTestSuite } from '@/modules/ui-test/utils/uiTestRunner'
import type { UiTestScenarioSummary } from '@/modules/ui-test/uiTest.types'

export type VisualRun = {
  summary: UiTestScenarioSummary
  shots: TesterShot[]
}

export type VisualScenarioCtx = {
  uiTest: UiTestRunner
  shots: TesterShot[]
  snap: (node: HTMLElement, label: string) => Promise<TesterShot>
  screen: () => HTMLElement
}

export function createVisualCtx(uiTest: UiTestRunner): VisualScenarioCtx {
  const shots: TesterShot[] = []
  const snap = async (node: HTMLElement, label: string) => {
    const shot = await captureNode(node, label)
    shots.push(shot)
    return shot
  }
  const screen = () => {
    const root = uiTest.screenRootRef.current
    if (!root) throw new Error('Không thấy vùng nội dung màn hình')
    return root
  }
  return { uiTest, shots, snap, screen }
}

export function withShot<T extends { status: 'ok' | 'skip' | 'fail'; message: string; evaluation?: string }>(
  interaction: T,
  shot: TesterShot | null,
): T {
  if (interaction.status === 'skip') return interaction
  if (!shot?.dataUrl) {
    return {
      ...interaction,
      status: 'fail' as const,
      message: `${interaction.message} Không chụp được ảnh${shot?.error ? `: ${shot.error}` : ''}.`,
    }
  }
  return interaction
}

export function btnLabel(btn: Element): string {
  return `${btn.getAttribute('aria-label') ?? ''} ${btn.textContent ?? ''}`.replace(/\s+/g, ' ').trim()
}

export async function goCleanList(
  uiTest: UiTestRunner,
  listPath: string,
  opts?: { ready?: (screen: HTMLElement) => boolean },
): Promise<void> {
  const qIndex = listPath.indexOf('?')
  const pathOnly = qIndex >= 0 ? listPath.slice(0, qIndex) : listPath
  const fixed = qIndex >= 0 ? listPath.slice(qIndex + 1) : ''
  const targetUrl = fixed ? `${pathOnly}?${fixed}` : pathOnly

  const currentClean =
    window.location.pathname === pathOnly &&
    !window.location.search.includes('uiTester') &&
    (fixed ? window.location.search.includes(fixed.split('&')[0]!) : window.location.search === '')

  if (!currentClean) {
    uiTest.navigate(targetUrl, { replace: true })
    await waitFor(() => {
      if (window.location.pathname !== pathOnly) return false
      return !window.location.search.includes('uiTester')
    }, 8000, 100)
  }

  await waitFor(() => {
    const screen = uiTest.screenRootRef.current
    if (!screen) return false
    const loading = Array.from(screen.querySelectorAll('[role="progressbar"]')).some(
      (bar) => !bar.closest('[data-ui-test-overlay="true"]'),
    )
    if (loading) return false
    if (opts?.ready) return opts.ready(screen)
    if (screen.querySelector('table')) return true
    const text = screen.textContent ?? ''
    return text.includes('Không có dữ liệu') || text.includes('Tổng số 0')
  }, 20_000, 200)
}

export function probeCode(screen: HTMLElement, preferredHeaders?: string[]): string | null {
  const byAttr = screen.querySelector('tbody tr[data-ui-test-row-ma]') as HTMLElement | null
  const attr = byAttr?.getAttribute('data-ui-test-row-ma')?.trim()
  if (attr) return attr

  const table = screen.querySelector('table')
  if (!table) return null
  const headers = Array.from(table.querySelectorAll('thead th')).map((th) =>
    (th.textContent ?? '').replace(/\s+/g, ' ').trim(),
  )
  let codeIdx = -1
  if (preferredHeaders?.length) {
    for (const h of preferredHeaders) {
      codeIdx = headers.findIndex((x) => x === h || x.includes(h))
      if (codeIdx >= 0) break
    }
  }
  if (codeIdx < 0) {
    codeIdx = headers.findIndex((h) => /mã|code|username/i.test(h))
  }

  for (const row of Array.from(table.querySelectorAll('tbody tr'))) {
    const cells = row.querySelectorAll('td')
    if (cells.length < 2) continue
    const pick =
      codeIdx >= 0
        ? cells[codeIdx]
        : Array.from(cells).find((c) => {
            const t = (c.textContent ?? '').replace(/\s+/g, ' ').trim()
            return t && t !== '—' && !/^(\d+|Tổng)/.test(t) && t.length >= 2 && t.length <= 48
          })
    const text = (pick?.textContent ?? '').replace(/\s+/g, ' ').trim()
    if (text && text !== '—' && !text.startsWith('Tổng')) return text
  }
  return null
}

export function findSearchInput(screen: HTMLElement, prefix?: string): HTMLInputElement | null {
  if (prefix) {
    const bar = screen.querySelector(`[data-ui-test-id="${prefix}-search-bar"]`)
    const inBar = bar?.querySelector('input:not([type="hidden"])') as HTMLInputElement | null
    if (inBar) return inBar
  }
  return (
    (screen.querySelector(
      'input[placeholder*="Tìm"], input[placeholder*="Theo"], input[placeholder*="Lọc"], input[type="search"]',
    ) as HTMLInputElement | null) ?? null
  )
}

export function findAddButton(screen: HTMLElement, prefix?: string): HTMLElement | null {
  if (prefix) {
    const byId = findInRoot(screen, `[data-ui-test-id="${prefix}-add-btn"]`)
    if (byId) return byId
  }
  return (
    (Array.from(screen.querySelectorAll('button')).find((btn) => {
      const label = btnLabel(btn)
      return /^(thêm|tạo)\b/i.test(label) && !/nhóm|cấp dưới|tab/i.test(label)
    }) as HTMLElement | undefined) ?? null
  )
}

export function findRowEdit(screen: HTMLElement, ma: string, prefix?: string): HTMLElement | null {
  const row =
    (screen.querySelector(`tbody tr[data-ui-test-row-ma="${CSS.escape(ma)}"]`) as HTMLElement | null) ??
    (Array.from(screen.querySelectorAll('tbody tr')).find((r) => (r.textContent ?? '').includes(ma)) as
      | HTMLElement
      | undefined) ??
    null
  if (!row) return null
  if (prefix) {
    const byId = row.querySelector(`[data-ui-test-id="${prefix}-edit-${CSS.escape(ma)}"] button`) as HTMLElement | null
    if (byId) return byId
  }
  return (
    (Array.from(row.querySelectorAll('button')).find((btn) => /sửa|chỉnh sửa|edit/i.test(btnLabel(btn))) as
      | HTMLElement
      | undefined) ?? null
  )
}

export async function closeTopDialog(): Promise<void> {
  if (document.querySelector('[role="listbox"]')) {
    document.body.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
    await waitFor(() => document.querySelector('[role="listbox"]') == null, 2000, 80).catch(() => undefined)
  }
  const dialog = await waitForTopDialog(5000)
  const cancel = findButton(dialog, 'Hủy') ?? findButton(dialog, 'Đóng')
  const closeIcon = dialog.querySelector('button[aria-label="Đóng"]') as HTMLButtonElement | null
  const btn = cancel ?? closeIcon
  if (!btn) {
    document.body.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
    await waitForDialogClosed(dialog, 5000).catch(() => undefined)
    return
  }
  await clickElement(btn)
  await waitForDialogClosed(dialog, 8000)
}

export async function openFilterPopover(screen: HTMLElement, prefix?: string): Promise<HTMLElement | null> {
  const bar = prefix
    ? screen.querySelector(`[data-ui-test-id="${prefix}-search-bar"]`)
    : findSearchInput(screen)?.closest('div')
  const filterBtn =
    (bar?.querySelector(
      'button[aria-label="Lọc"], button[aria-label*="Lọc"], button[aria-label*="Filter"], button[aria-label*="bộ lọc"]',
    ) as HTMLButtonElement | null) ??
    (screen.querySelector(
      'button[aria-label="Lọc"], button[aria-label*="Lọc"], button[aria-label*="Filter"]',
    ) as HTMLButtonElement | null) ??
    (Array.from((bar ?? screen).querySelectorAll('button')).find((b) =>
      /lọc|filter/i.test(btnLabel(b)),
    ) as HTMLButtonElement | undefined) ??
    null
  if (!filterBtn) return null
  await clickElement(filterBtn)
  await waitFor(() => Boolean(document.querySelector('.MuiPopover-paper, [role="presentation"] .MuiPaper-root')), 5000, 80)
  return (
    (document.querySelector('.MuiPopover-paper') as HTMLElement | null) ??
    (document.querySelector('[role="presentation"] .MuiPaper-root') as HTMLElement | null)
  )
}

export function assertFormFields(dialog: HTMLElement, fieldNames: string[]): string[] {
  const missing: string[] = []
  for (const name of fieldNames) {
    const el = dialog.querySelector(`input[name="${name}"], textarea[name="${name}"], [name="${name}"]`)
    if (!el) missing.push(name)
  }
  return missing
}

export async function clickSidebarOption(screen: HTMLElement, label: string): Promise<boolean> {
  const sidebar = sidebarOf(screen)
  const root = sidebar ?? screen
  const byLabel = Array.from(root.querySelectorAll('label')).find(
    (n) => (n.textContent ?? '').replace(/\s+/g, ' ').trim() === label,
  ) as HTMLElement | undefined
  if (byLabel) {
    await clickElement(byLabel)
    return true
  }
  const radio = root.querySelector(`input[type="radio"][value="${label}"]`) as HTMLElement | null
  if (radio) {
    await clickElement(radio)
    return true
  }
  const btn = Array.from(root.querySelectorAll('button')).find(
    (b) => (b.textContent ?? '').replace(/\s+/g, ' ').trim().includes(label),
  ) as HTMLElement | undefined
  if (btn) {
    await clickElement(btn)
    return true
  }
  return false
}

export async function expandFirstDataRow(screen: HTMLElement): Promise<boolean> {
  const row =
    (screen.querySelector('tbody tr[data-ui-test-row-ma]') as HTMLElement | null) ??
    (Array.from(screen.querySelectorAll('tbody tr')).find((r) => r.querySelectorAll('td').length >= 3) as
      | HTMLElement
      | undefined) ??
    null
  if (!row) return false
  await clickElement(row)
  await waitFor(() => {
    const expanded =
      screen.querySelector('[aria-expanded="true"]') ||
      screen.querySelector('[class*="Expand"], [class*="expand"]') ||
      screen.querySelector('[data-kiot-expand]')
    return Boolean(expanded) || (screen.textContent ?? '').length > 0
  }, 4000, 100).catch(() => undefined)
  return true
}

/** Feature builders — gắn vào kịch bản module. */
export function featureList(opts: {
  id?: string
  listPath: string
  label: string
  prefix?: string
  expectAdd?: boolean
  expectSearch?: boolean
  columnHeaders?: string[]
}): UiTestFeatureDef {
  return {
    id: opts.id ?? 'list',
    label: 'Danh sách',
    run: async (uiTest) => {
      const ctx = createVisualCtxCollected(uiTest)
      await goCleanList(uiTest, opts.listPath)
      const screen = ctx.screen()
      const table = screen.querySelector('table')
      if (!table) return { status: 'fail' as const, message: `Không thấy bảng ${opts.label}` }
      const shot = await ctx.snap(screen, `Danh sách ${opts.label}`)
      const issues: string[] = []
      if (opts.expectSearch !== false && !findSearchInput(screen, opts.prefix)) issues.push('thiếu ô tìm')
      if (opts.expectAdd && !findAddButton(screen, opts.prefix)) issues.push('thiếu nút Thêm')
      if (opts.columnHeaders?.length) {
        const head = (table.querySelector('thead')?.textContent ?? '').replace(/\s+/g, ' ')
        for (const h of opts.columnHeaders) {
          if (!head.includes(h)) issues.push(`thiếu cột "${h}"`)
        }
      }
      return withShot(
        {
          status: issues.length ? 'fail' : 'ok',
          message: issues.length ? issues.join('; ') : `Bảng ${opts.label} đã hiện đủ chrome cơ bản`,
          evaluation: issues.length ? `Cần sửa: ${issues.join('; ')}.` : undefined,
        },
        shot,
      )
    },
  }
}

export function featureSearch(opts: {
  id?: string
  listPath: string
  label: string
  prefix?: string
  codeHeaders?: string[]
}): UiTestFeatureDef {
  return {
    id: opts.id ?? 'search',
    label: 'Ô tìm kiếm',
    run: async (uiTest) => {
      const ctx = createVisualCtxCollected(uiTest)
      await goCleanList(uiTest, opts.listPath)
      const screen = ctx.screen()
      const probe = probeCode(screen, opts.codeHeaders)
      if (!probe) return { status: 'skip' as const, message: 'Không có dòng để tìm' }
      const input = findSearchInput(screen, opts.prefix)
      if (!input) return { status: 'fail' as const, message: 'Không thấy ô tìm' }
      await clickElement(input)
      await typeSlowly(input, probe)
      input.focus()
      await pressEnter(input)
      await waitFor(() => (ctx.screen().textContent ?? '').includes(probe), 10_000, 150).catch(() => undefined)
      const shot = await ctx.snap(ctx.screen(), `Tìm "${probe}" — ${opts.label}`)
      const ok = (ctx.screen().textContent ?? '').includes(probe)
      return withShot(
        {
          status: ok ? 'ok' : 'fail',
          message: ok ? `Gõ "${probe}", Enter, còn thấy dòng` : `Tìm "${probe}" nhưng mất dòng`,
        },
        shot,
      )
    },
  }
}

export function featureFilterPopover(opts: {
  id?: string
  listPath: string
  label: string
  prefix?: string
  expectedLabels: string[]
}): UiTestFeatureDef {
  return {
    id: opts.id ?? 'filter-popover',
    label: 'Bộ lọc nâng cao',
    run: async (uiTest) => {
      const ctx = createVisualCtxCollected(uiTest)
      await goCleanList(uiTest, opts.listPath)
      const paper = await openFilterPopover(ctx.screen(), opts.prefix)
      if (!paper) return { status: 'skip' as const, message: 'Không có nút Lọc / popover' }
      const shot = await ctx.snap(paper, `Popover lọc ${opts.label}`)
      const text = paper.textContent ?? ''
      const missing = opts.expectedLabels.filter((l) => !text.includes(l))
      const reset = findButton(paper, 'Đặt lại') ?? findButton(paper, 'Hủy')
      if (reset) await clickElement(reset)
      else document.body.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
      return withShot(
        {
          status: missing.length ? 'fail' : 'ok',
          message: missing.length
            ? `Popover thiếu: ${missing.join(', ')}`
            : `Popover lọc có đủ: ${opts.expectedLabels.join(', ')}`,
          evaluation: missing.length ? `Cần sửa: Popover thiếu ${missing.join(', ')}.` : undefined,
        },
        shot,
      )
    },
  }
}

export function featureSidebarStatus(opts: {
  id?: string
  listPath: string
  label: string
  activeLabel?: string
  allLabel?: string
}): UiTestFeatureDef {
  const activeLabel = opts.activeLabel ?? 'Đang hoạt động'
  const allLabel = opts.allLabel ?? 'Tất cả'
  return {
    id: opts.id ?? 'status-filter',
    label: 'Lọc trạng thái',
    run: async (uiTest) => {
      const ctx = createVisualCtxCollected(uiTest)
      await goCleanList(uiTest, opts.listPath)
      const ok = await clickSidebarOption(ctx.screen(), activeLabel)
      if (!ok) return { status: 'skip' as const, message: `Sidebar không có "${activeLabel}"` }
      await waitFor(() => window.location.search.includes('trangThai') || window.location.search.length > 0, 5000, 100).catch(
        () => undefined,
      )
      const shot = await ctx.snap(ctx.screen(), `Lọc ${activeLabel} — ${opts.label}`)
      await clickSidebarOption(ctx.screen(), allLabel)
      return withShot(
        { status: 'ok', message: `Chọn "${activeLabel}" rồi về "${allLabel}"` },
        shot,
      )
    },
  }
}

export function featureOpenAddCancel(opts: {
  id?: string
  listPath: string
  label: string
  prefix?: string
  formFields?: string[]
  addButtonExtra?: string
}): UiTestFeatureDef {
  return {
    id: opts.id ?? 'create-form',
    label: 'Form thêm',
    run: async (uiTest) => {
      const ctx = createVisualCtxCollected(uiTest)
      await goCleanList(uiTest, opts.listPath)
      let add = findAddButton(ctx.screen(), opts.prefix)
      if (!add && opts.addButtonExtra) {
        add =
          (Array.from(ctx.screen().querySelectorAll('button')).find((b) =>
            btnLabel(b).includes(opts.addButtonExtra!),
          ) as HTMLElement | undefined) ?? null
      }
      if (!add) return { status: 'fail' as const, message: 'Không thấy nút Thêm' }
      await clickElement(add)
      let dialog: HTMLElement
      try {
        dialog = await waitForTopDialog(10_000)
      } catch {
        return { status: 'fail' as const, message: 'Bấm Thêm nhưng không mở dialog/form' }
      }
      const missing = opts.formFields?.length ? assertFormFields(dialog, opts.formFields) : []
      const chrome = inspectFormChrome(dialog)
      const shot = await ctx.snap(dialog, `Form thêm ${opts.label}`)
      await closeTopDialog()
      const issues = [
        ...missing.map((f) => `thiếu ô ${f}`),
        ...(!chrome.footerOutsideBody ? ['thiếu footer Hủy/Lưu'] : []),
      ]
      return withShot(
        {
          status: issues.length ? 'fail' : 'ok',
          message: issues.length
            ? issues.join('; ')
            : `Mở form thêm [${(opts.formFields ?? chrome.fieldLabels).slice(0, 6).join(', ')}], Hủy không lưu`,
          evaluation: issues.length ? `Cần sửa: ${issues.join('; ')}.` : undefined,
        },
        shot,
      )
    },
  }
}

export function featureOpenEditCancel(opts: {
  id?: string
  listPath: string
  label: string
  prefix?: string
  codeHeaders?: string[]
  formFields?: string[]
}): UiTestFeatureDef {
  return {
    id: opts.id ?? 'edit-form',
    label: 'Form sửa',
    run: async (uiTest) => {
      const ctx = createVisualCtxCollected(uiTest)
      await goCleanList(uiTest, opts.listPath)
      const probe = probeCode(ctx.screen(), opts.codeHeaders)
      if (!probe) return { status: 'skip' as const, message: 'Không có dòng để sửa' }
      const input = findSearchInput(ctx.screen(), opts.prefix)
      if (input) {
        await clickElement(input)
        await typeSlowly(input, probe)
        input.focus()
        await pressEnter(input)
        await waitFor(() => Boolean(findRowEdit(ctx.screen(), probe, opts.prefix)), 8000, 150).catch(() => undefined)
      }
      const edit = findRowEdit(ctx.screen(), probe, opts.prefix)
      if (!edit) return { status: 'skip' as const, message: `Dòng ${probe} không có nút Sửa trên list` }
      await clickElement(edit)
      let dialog: HTMLElement
      try {
        dialog = await waitForTopDialog(12_000)
      } catch {
        const shot = await ctx.snap(ctx.screen(), `Sau Sửa ${probe} — ${opts.label}`)
        return withShot(
          { status: 'ok', message: `Bấm Sửa ${probe} — điều hướng/form ngoài dialog` },
          shot,
        )
      }
      const missing = opts.formFields?.length ? assertFormFields(dialog, opts.formFields) : []
      const shot = await ctx.snap(dialog, `Form sửa ${probe} — ${opts.label}`)
      await closeTopDialog()
      return withShot(
        {
          status: missing.length ? 'fail' : 'ok',
          message: missing.length
            ? `Form sửa thiếu: ${missing.join(', ')}`
            : `Sửa ${probe}: mở form, Hủy không lưu`,
          evaluation: missing.length ? `Cần sửa: thiếu ${missing.join(', ')}.` : undefined,
        },
        shot,
      )
    },
  }
}

export function featureExpandRow(opts: {
  id?: string
  listPath: string
  label: string
}): UiTestFeatureDef {
  return {
    id: opts.id ?? 'expand-row',
    label: 'Xem chi tiết dòng',
    run: async (uiTest) => {
      const ctx = createVisualCtxCollected(uiTest)
      await goCleanList(uiTest, opts.listPath)
      const ok = await expandFirstDataRow(ctx.screen())
      if (!ok) return { status: 'skip' as const, message: 'Không có dòng để mở rộng' }
      const shot = await ctx.snap(ctx.screen(), `Mở rộng dòng — ${opts.label}`)
      return withShot({ status: 'ok', message: 'Click dòng để xem panel chi tiết (không lưu)' }, shot)
    },
  }
}

export function featureImportOpenClose(opts: {
  id?: string
  listPath: string
  label: string
}): UiTestFeatureDef {
  return {
    id: opts.id ?? 'import',
    label: 'Import',
    run: async (uiTest) => {
      const ctx = createVisualCtxCollected(uiTest)
      await goCleanList(uiTest, opts.listPath)
      const importBtn = Array.from(ctx.screen().querySelectorAll('button')).find((b) =>
        /import|nhập/i.test(btnLabel(b)),
      ) as HTMLElement | undefined
      if (!importBtn) return { status: 'skip' as const, message: 'Không thấy nút Import' }
      await clickElement(importBtn)
      await waitFor(
        () => Boolean(document.querySelector('[role="dialog"], .MuiPopover-paper, .MuiMenu-paper')),
        5000,
        80,
      ).catch(() => undefined)
      const panel =
        (document.querySelector('[role="dialog"]') as HTMLElement | null) ??
        (document.querySelector('.MuiPopover-paper, .MuiMenu-paper') as HTMLElement | null)
      const shot = await ctx.snap(panel ?? ctx.screen(), `Import — ${opts.label}`)
      if (panel?.getAttribute('role') === 'dialog') await closeTopDialog().catch(() => undefined)
      else document.body.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
      return withShot({ status: 'ok', message: 'Mở Import/menu nhập, đóng không submit' }, shot)
    },
  }
}

export function featureClickTab(opts: {
  id: string
  listPath: string
  label: string
  tabText: string
}): UiTestFeatureDef {
  return {
    id: opts.id,
    label: `Tab ${opts.tabText}`,
    run: async (uiTest) => {
      const ctx = createVisualCtxCollected(uiTest)
      await goCleanList(uiTest, opts.listPath)
      const tab = Array.from(ctx.screen().querySelectorAll('button, [role="tab"]')).find((el) =>
        (el.textContent ?? '').replace(/\s+/g, ' ').trim().includes(opts.tabText),
      ) as HTMLElement | undefined
      if (!tab) return { status: 'fail' as const, message: `Không thấy tab "${opts.tabText}"` }
      await clickElement(tab)
      const shot = await ctx.snap(ctx.screen(), `Tab ${opts.tabText} — ${opts.label}`)
      return withShot({ status: 'ok', message: `Chuyển tab "${opts.tabText}"` }, shot)
    },
  }
}

export async function runVisualFeatures(
  uiTest: UiTestRunner,
  features: UiTestFeatureDef[],
  suiteLabel: string,
): Promise<VisualRun> {
  const shots: TesterShot[] = []
  const prev = (window as unknown as { __visualShots?: TesterShot[] }).__visualShots
  ;(window as unknown as { __visualShots: TesterShot[] }).__visualShots = shots
  try {
    const summary = await runUiTestSuite(uiTest, features, suiteLabel)
    return { summary, shots }
  } finally {
    ;(window as unknown as { __visualShots?: TesterShot[] }).__visualShots = prev
  }
}

/** createVisualCtx đẩy shot vào cả local + window collector. */
export function createVisualCtxCollected(uiTest: UiTestRunner): VisualScenarioCtx {
  const local: TesterShot[] = []
  const snap = async (node: HTMLElement, label: string) => {
    const shot = await captureNode(node, label)
    local.push(shot)
    const bag = (window as unknown as { __visualShots?: TesterShot[] }).__visualShots
    if (bag) bag.push(shot)
    return shot
  }
  const screen = () => {
    const root = uiTest.screenRootRef.current
    if (!root) throw new Error('Không thấy vùng nội dung màn hình')
    return root
  }
  return { uiTest, shots: local, snap, screen }
}
